import { useCallback, useMemo } from "react";
import { indexEntityProposals, pendingFieldProposals } from "src/components/formFields/ai/utils/aiProposalForm";
import { AiFormAepFragment } from "src/components/formFields/ai/utils/aiProposalForm.generated";
import { parseAiNumber, parseAiString } from "src/components/formFields/ai/utils/bindAiProposal";
import { useFeatureFlag } from "src/contexts/FeatureFlags/FeatureFlagContext";
import {
  AiEntityProposalType,
  AiFieldProposalStatus,
  FeatureFlagType,
  SaveTakeoffLineItemInput,
} from "src/generated/graphqlTypes";
import { useTakeoffProposalsQuery } from "src/routes/libraries/plan-package-v2/components/takeoffs/useTakeoffProposals.generated";
import { PlanPackagePageV2MetadataDocument } from "src/routes/libraries/plan-package-v2/PlanPackagePageV2.generated";
import { useTakeoffsStore } from "src/routes/libraries/plan-package/takeoffs/TakeoffsManagerContext";
import { fail } from "src/utils";

export type ReviewDecision = AiFieldProposalStatus.Accepted | AiFieldProposalStatus.Rejected;

/** The plan's takeoff proposals with pending fields, keyed by line id. Not paginated, so it covers unloaded groups. */
export function useTakeoffProposals() {
  const planPackageId = useTakeoffsStore((state) => state.planPackage.id);
  const versionId = useTakeoffsStore((state) => state.planPackage.version.id);
  const isTakeoffAiEnabled = useFeatureFlag(FeatureFlagType.PlanPackagesTakeoffAi);
  const { data, loading } = useTakeoffProposalsQuery({
    variables: { id: planPackageId, versionId },
    skip: !isTakeoffAiEnabled,
  });
  const proposalsByTliId = useMemo(() => {
    const pending = data?.planPackage.aiProposal?.entityProposals.filter(
      (aep) => pendingFieldProposals(aep.fieldProposals).nonEmpty,
    );
    return indexEntityProposals(pending, "TakeoffLineItem");
  }, [data]);
  return { proposalsByTliId, loading };
}

type ResolveOpts = {
  /**
   * TLI inputs to save instead of the decision's, i.e. the Qty the user typed, or a delete.
   * They don't need proposals, i.e. a bulk delete can include lines the AI never touched.
   */
  lines?: SaveTakeoffLineItemInput[];
  /** Fields recorded Rejected whatever the decision, i.e. a proposal the user typed over. */
  rejectFields?: string[];
};

/**
 * Accepts or ignores proposals. Each TLI saves its changed fields, i.e. the actual quantity change, as well as
 * resolving the proposal that proposed the change, in one transaction, so a failed save leaves every proposal pending.
 */
export function useResolveTakeoffProposals() {
  const saveTli = useTakeoffsStore((state) => state.saveTli);
  return useCallback(
    (aeps: AiFormAepFragment[], decision: ReviewDecision, opts: ResolveOpts = {}) => {
      const { lines = [], rejectFields = [] } = opts;
      const aepsByTliId = new Map(aeps.map((aep) => [aep.entity.id, aep]));
      // What the decision writes to each TLI, replaced by the caller's input when there is one
      const inputsByTliId = new Map([
        ...aeps.map((aep) => [aep.entity.id, toTliInput(aep, decision)] as const),
        ...lines.map((line) => [line.id, line] as const),
      ]);
      // Merges each TLI input with the proposals it resolves
      const input = [...inputsByTliId.values()].map((tli) => {
        const aep = tli.id ? aepsByTliId.get(tli.id) : undefined;
        if (!aep) return tli;
        const proposals = pendingFieldProposals(aep.fieldProposals).map(({ id, fieldName }) => ({
          id,
          status: rejectFields.includes(fieldName) ? AiFieldProposalStatus.Rejected : decision,
        }));
        return { ...tli, proposals };
      });
      // The page query drives the tab's AI badge
      return saveTli(input, { refetchQueries: [PlanPackagePageV2MetadataDocument] });
    },
    [saveTli],
  );
}

/**
 * Deactivates lines for `DeleteItemsConfirmationModal`, rejecting any proposals pending for them in the same save,
 * so the banner and AI-Generated filter don't keep counting a line that's gone.
 */
export function useDeleteTakeoffLines() {
  const saveTli = useTakeoffsStore((state) => state.saveTli);
  const { proposalsByTliId } = useTakeoffProposals();
  const resolve = useResolveTakeoffProposals();
  return useCallback(
    (tliIds: string[]) => {
      const lines = tliIds.map((id) => ({ id, active: false }));
      const aeps = tliIds.map((id) => proposalsByTliId.get(id)).compact();
      // Lines with nothing proposed skip the AI badge's refetch
      return aeps.nonEmpty ? resolve(aeps, AiFieldProposalStatus.Rejected, { lines }) : saveTli(lines);
    },
    [saveTli, proposalsByTliId, resolve],
  );
}

/** Creates an input that applies all of the AEP's changes, or for an ignore, leaves the TLI as it was before the AI. */
export function toTliInput(aep: AiFormAepFragment, decision: ReviewDecision): SaveTakeoffLineItemInput {
  const { id } = aep.entity;
  const accepted = decision === AiFieldProposalStatus.Accepted;
  // The import already created a CREATE's line, so accepting keeps it as-is and ignoring removes it
  if (aep.type.code === AiEntityProposalType.Create) return accepted ? { id } : { id, active: false };
  // The backend only proposes CREATEs and UPDATEs for lines, so anything else would resolve without being applied
  if (aep.type.code !== AiEntityProposalType.Update) fail(`Unexpected ${aep.type.code} proposal for ${id}`);
  if (!accepted) return { id };
  // An UPDATE leaves the line as-is, so accepting writes the proposed quantity, UoM, and task
  const quantity = parseAiNumber(pendingUpdate(aep, "quantity"));
  const unitOfMeasureName = parseAiString(pendingUpdate(aep, "unitOfMeasure"));
  const taskName = parseAiString(pendingUpdate(aep, "task"));
  return {
    id,
    ...(quantity !== undefined ? { quantity } : {}),
    ...(unitOfMeasureName ? { unitOfMeasureName } : {}),
    ...(taskName ? { taskName } : {}),
  };
}

/** An UPDATE's pending value for one field. The backend only proposes updates to a line's Qty, UoM and Task. */
export function pendingUpdate(
  aep: AiFormAepFragment | undefined,
  fieldName: "quantity" | "unitOfMeasure" | "task",
): string | undefined {
  if (aep?.type.code !== AiEntityProposalType.Update) return undefined;
  return pendingFieldProposals(aep.fieldProposals).find((fp) => fp.fieldName === fieldName)?.fieldValue ?? undefined;
}
